/**
 * A suggestion's transactions (TMMAPP-0136 item 9; TMMAPI-0170): `GET /v1/me/payees/suggestions/{id}
 * /lines`, newest first — the lines an accept would name. Each row is the date, the account, the
 * signed amount (body face, tabular) and the raw bank text (mono). Load more walks `nextCursor`;
 * "Showing {shown} of {count}" is a polite live region, and Load more focuses the first new row.
 * 720 wide, full-screen below `sm`. Accept, Edit and Forget suggestion stay on the card.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { payeeSuggestionsApi, type PayeeSuggestionLine } from '@/api/payeeSuggestions';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';

const PAGE_SIZE = 50;

interface SuggestionLinesDialogProps {
  suggestionId: number;
  /** The bank text (or, for an identity key, the counterparty's display name) the card shows. */
  bankText: string;
  /** The card's served line count, for "Showing {shown} of {count}". */
  lineCount: number | null;
  onClose: () => void;
}

export function SuggestionLinesDialog({
  suggestionId,
  bankText,
  lineCount,
  onClose,
}: SuggestionLinesDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const [lines, setLines] = useState<PayeeSuggestionLine[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const firstNew = useRef<number | null>(null);
  const rowRefs = useRef(new Map<number, HTMLLIElement>());

  const loadFirst = useCallback(async () => {
    setFailure(null);
    try {
      const page = await payeeSuggestionsApi.lines(suggestionId, null, PAGE_SIZE);
      setLines(page.items);
      setCursor(page.nextCursor);
    } catch (error) {
      setFailure({ error });
    }
  }, [suggestionId]);

  // StrictMode double-invokes the effect; the first page is read once per suggestion.
  const loaded = useRef<number | null>(null);
  useEffect(() => {
    if (loaded.current === suggestionId) return;
    loaded.current = suggestionId;
    void loadFirst();
  }, [suggestionId, loadFirst]);

  useEffect(() => {
    if (firstNew.current === null) return;
    rowRefs.current.get(firstNew.current)?.focus();
    firstNew.current = null;
  }, [lines]);

  async function loadMore() {
    if (cursor === null || loadingMore || lines === null) return;
    setLoadingMore(true);
    setFailure(null);
    try {
      const page = await payeeSuggestionsApi.lines(suggestionId, cursor, PAGE_SIZE);
      firstNew.current = page.items[0]?.id ?? null;
      setLines([...lines, ...page.items]);
      setCursor(page.nextCursor);
    } catch (error) {
      setFailure({ error });
    } finally {
      setLoadingMore(false);
    }
  }

  const shown = lines?.length ?? 0;
  const count = Math.max(lineCount ?? 0, shown);

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      aria-labelledby={titleId}
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 720 } }}
      data-suggestion-lines=""
    >
      <DialogTitle id={titleId} sx={{ overflowWrap: 'anywhere' }}>
        {t('naming.linesTitle', { bank: bankText })}
      </DialogTitle>
      <DialogContent>
        {failure !== null && (
          <ErrorState
            variant="section"
            error={failure.error}
            what={t('errors.what.suggestionLines')}
            onRetry={() => void (lines === null ? loadFirst() : loadMore())}
          />
        )}
        {lines === null && failure === null && (
          <Skeleton variant="rounded" height={120} aria-busy="true" />
        )}
        {lines !== null && (
          <Stack spacing={1.5}>
            <Typography
              variant="body2"
              role="status"
              aria-live="polite"
              sx={{ color: tokens.text2 }}
            >
              {t('naming.linesShowing', { shown, count })}
            </Typography>
            <Box
              component="ul"
              aria-label={t('naming.linesTitle', { bank: bankText })}
              sx={{ listStyle: 'none', m: 0, p: 0 }}
            >
              {lines.map((line) => (
                <Box
                  component="li"
                  key={line.id}
                  tabIndex={-1}
                  ref={(node: HTMLLIElement | null) => {
                    if (node === null) rowRefs.current.delete(line.id);
                    else rowRefs.current.set(line.id, node);
                  }}
                  data-suggestion-line={line.id}
                  sx={{
                    py: 1,
                    borderTop: `1px solid ${tokens.line}`,
                    '&:first-of-type': { borderTop: 0 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr auto', sm: '110px minmax(0, 1fr) auto' },
                    columnGap: 2,
                    rowGap: 0.5,
                    alignItems: 'baseline',
                    '&:focus-visible': { outline: `2px solid ${tokens.focusRing}` },
                  }}
                >
                  <Typography variant="body2" sx={{ color: tokens.text2 }}>
                    {formatCivilDate(line.postedOn, locale)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      gridColumn: { xs: '1 / -1', sm: 'auto' },
                      gridRow: { xs: 2, sm: 'auto' },
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {line.accountName}
                  </Typography>
                  <Box sx={{ textAlign: 'end' }}>
                    <MoneyText minor={line.amountMinor} currency={line.currency} variant="body2" />
                  </Box>
                  <Typography
                    variant="mono"
                    component="span"
                    sx={{
                      gridColumn: '1 / -1',
                      color: tokens.text2,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {line.payee}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {cursor !== null && lines !== null && (
          <SubmitButton variant="outlined" loading={loadingMore} onClick={() => void loadMore()}>
            {t('common.loadMore')}
          </SubmitButton>
        )}
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
